import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { BellRing } from 'lucide-react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog.jsx';

const MESSAGES_PATH = '/subcontractor/dashboard/messages';
export const isOtherAudience = (audience) => audience === 'admin' || audience === 'partner';

/**
 * Shows a confirm dialog when the user taps a push notification, then lets them
 * jump to their messages or dismiss and carry on. Two entry points:
 *  - App already open: the service worker postMessages PUSH_NOTIFICATION_CLICK.
 *  - App was closed: the SW opens it with ?push=1 and we prompt on load.
 */
const PushClickDialog = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [payload, setPayload] = useState({ title: 'New notification', body: '', url: MESSAGES_PATH });

  const show = useCallback((p) => {
    setPayload({
      title: p?.title || 'New notification',
      body: p?.body || '',
      url: p?.url || MESSAGES_PATH,
    });
    setOpen(true);
  }, []);

  // App running → service worker hands us the clicked notification.
  useEffect(() => {
    if (typeof navigator === 'undefined' || !navigator.serviceWorker) return undefined;
    const handler = (event) => {
      // An admin's or a partner's push (a shared phone, 20261327) is not the crew's to show.
      if (event?.data?.type === 'PUSH_NOTIFICATION_CLICK' && !isOtherAudience(event.data.audience)) show(event.data);
    };
    navigator.serviceWorker.addEventListener('message', handler);
    return () => navigator.serviceWorker.removeEventListener('message', handler);
  }, [show]);

  // App opened cold from a notification (?push=1) → prompt, then clean the URL.
  // The service worker now launches the app AT the notification's own link (deep
  // links like the research invite), so "view" keeps the user where they landed
  // instead of yanking them to messages.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('push') === '1') {
      params.delete('push');
      const here = `${location.pathname}${params.toString() ? `?${params.toString()}` : ''}`;
      show({ title: 'New notification', body: 'You have a new notification.', url: here });
      navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
    }
    // run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the query string — deep links carry tab focus (e.g. ?tab=research).
  const toPath = (url) => {
    try {
      const u = new URL(url, window.location.origin);
      return `${u.pathname || MESSAGES_PATH}${u.search || ''}`;
    } catch { return MESSAGES_PATH; }
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <BellRing className="h-5 w-5 text-rose-500" />
            {payload.title}
          </AlertDialogTitle>
          {payload.body && <AlertDialogDescription>{payload.body}</AlertDialogDescription>}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Dismiss</AlertDialogCancel>
          <AlertDialogAction onClick={() => { setOpen(false); navigate(toPath(payload.url)); }}>
            {toPath(payload.url).startsWith(MESSAGES_PATH) ? 'View messages' : 'View'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default PushClickDialog;
